<template>
  <li class="section">
    <legend>
      <span class="h3" id="partyOneDetails">Person {{ number }}'s details</span>
    </legend>
    <ol class="questions">
      <fieldset>
        <li>
          <legend>
            <span class="label">Is person {{ number }} known by one name?</span>
            <abbr :id="`party${number}OneName`" class="required" title="(required)">*</abbr>
            <small class="hint"
              >Select 'Yes' if person {{ number }} only has one name and enter
              the name in the family name field.</small
            >
          </legend>
          <ul class="choices compact">
            <li>
              <ValidationProvider :rules="`required:#party${number}OneName`">
                <input
                  type="radio"
                  :id="`party${number}OneName.yes`"
                  :name="`Party ${1} One Name?`"
                  value="true"
                  v-model='oneName'
                />
              </ValidationProvider>
              <label :for="`party${number}OneName.yes`">Yes</label>
            </li>
            <li>
              <input
                type="radio"
                :id="`party${number}OneName.no`"
                :name="`party${number}OneName`"
                value="false"
                v-model='oneName'
              />
              <label :for="`party${number}OneName.no`">No</label>
            </li>
          </ul>
        </li>
        <li v-if="oneName === 'false'">
          <label :for="`party${number}GivenNames`">
            <span class="label">Given name(s)</span>
          </label>

          <input
            autocomplete="off"
            class="titleCaseNameOnFirstBlur"
            :id="`party${number}GivenNames`"
            type="text"
            size="50"
            :name="`party${number}GivenNames`"
            maxlength="255"
            role="presentation"
            v-model='personDetails.givennames'
          />
        </li>
       
        <li>
          <InputWithError 
            label="Family Name"
            :id="`party${number}Surname`"
            :name="`Party ${number} Family Name`"
            size="50"
            maxlength="255"
            required="true"
            v-model='personDetails.surname'
          />
        </li>
        <li>
          <fieldset>
            <ol class="questions">
              <li>
                <ValidationProvider :rules="`required:#label-party${number}-description`">
                  <!-- use a hidden input to trick vue validate to work with select -->
                  <input 
                    type="text" 
                    class="form-control disabled" 
                    :name="`Party ${number} Description`" 
                    v-model="personDetails.description" 
                    style="display:none;"
                  />
                </ValidationProvider>
                <legend>
                  <label :for="`party${number}Description`">
                    <span class="label">How does person {{ number }} wish to be referred to
                    as?</span>
                    <abbr :id="`label-party${number}-description`" class="required" title="(required)">*</abbr>
                  </label>
                </legend>
                <select
                  :id="`party${number}Description`"
                  :name="`party${number}Description`"
                  v-model='uiDescription'
                  @click="syncSelects"
                >
                  <option selected disabled>- please select -</option>
                  <option value="bride">Bride</option>
                  <option value="groom">Groom</option>
                  <option value="partner">Partner</option>
                </select>
              </li>
              <li>
                <ValidationProvider :rules="`required:#label-party${number}-sex`">
                  <!-- use a hidden input to trick vue validate to work with select -->
                  <input 
                    type="text" 
                    class="form-control disabled" 
                    :name="`Party ${number} Gender`" 
                    v-model="personDetails.sex" 
                    style="display:none;"
                  />
                </ValidationProvider>
                <legend>
                  <label :for="`party${number}Sex`">
                    <span class="label">Gender</span>
                    <abbr :id="`label-party${number}-sex`" class="required" title="(required)">*</abbr>
                  </label>
                </legend>
                <select
                  :id="`party${number}Sex`"
                  :name="`party${number}Sex`"
                  v-model='uiGender'
                  @click="syncSelects"
                >
                  <option selected disabled>- please select -</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="partySexX">Non-binary</option>
                  <option value="partySexNS">Not stated</option>
                </select>
              </li>
            </ol>
          </fieldset>
        </li>

        <PersonAddressDetails
          v-model="personDetails.addressDetails"
          :number="number"
          @input="onInputChange"
          @copyAddress="copyAddress"
        />

        <OccupationSelect 
          :number="number"
          v-model="personDetails"
          @input="onInputChange"
        />

        <li>
          <InputWithError
            label="Date of birth"
            hint="Enter as dd/mm/yyyy"
            :id="`party${number}DateOfBirth`"
            :name="`Party ${number} Date of Birth`"
            size="10"
            type="date"
            placeholder="dd/mm/yyyy"
            maxlength="10"
            required="true"
            v-model='personDetails.dateOfBirth'
          />
        </li>

        <PersonUnderAgeOfConsent :number="number" v-if="isUnder18()" />

        <PersonBirthDetails
          v-model="personDetails"
          :number="number"
          @input="onInputChange"
        />

        <PersonConjugalDetails
          v-model="personDetails"
          :number="number"
          @input="onInputChange"
        />

        <li class="section">
          <fieldset>
            <legend>
              <span class="h3">Person {{ number }}'s parent's details</span>
            </legend>
            <PersonParentDetails :number="number" @input="onInputChange" v-model="personDetails" />
          </fieldset>
        </li>
      </fieldset>
    </ol>
  </li>
</template>

<script>
import PersonUnderAgeOfConsent from "./PersonDetails/PersonUnderAgeOfConsent.vue";
import PersonBirthDetails from "./PersonDetails/PersonBirthDetails.vue";
import PersonAddressDetails from "./PersonDetails/PersonAddressDetails.vue";
import PersonParentDetails from "./PersonDetails/PersonParentDetails.vue";
import PersonConjugalDetails from "./PersonDetails/PersonConjugalDetails.vue";
import OccupationSelect from "./PersonDetails/OccupationSelect.vue"
import InputWithError from "../InputWithError.vue"

export default {
  props: {
    number: String,
    value: Object,
  },
  components: {
    PersonUnderAgeOfConsent,
    PersonBirthDetails,
    PersonAddressDetails,
    PersonParentDetails,
    PersonConjugalDetails,
    OccupationSelect,
    InputWithError,
  },
  methods: {
    isUnder18() {
      var parts = this.personDetails.dateOfBirth.split('-');
      var ageDate = new Date(parts[0], parts[1] - 1, parts[2]);
      if (Math.abs(new Date().getFullYear() - ageDate.getFullYear()) < 18) {
        return true;
      }
      return false;
    },
    onInputChange(childPersonDetails) {
      this.form = { ...this.personDetails, ...childPersonDetails };
    },
    syncSelects() {
      this.personDetails.description = this.uiDescription;
      this.personDetails.sex = this.uiGender;
    },
    copyAddress(value) {
      this.$emit("copyAddress", value);
    },
  },
  data() {
    return {
      uiDescription: '',
      uiGender: '',
      oneName: "false"
    }
  },
  computed: {
    personDetails: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
};
</script>